import _ from 'lodash';
import { getComponentPropertyFieldDisplayName } from 'common/utils/componentPropertyFieldDisplayName';
import { getComponentTypeDisplayName } from 'common/utils/componentTypeDisplayName';

function toSlug(str) {
  return String(str || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function resolveMetadataOptionKey(opt) {
  if (opt == null) return null;
  if (typeof opt.value === 'string' && opt.value) return opt.value;
  if (typeof opt.value === 'number' && !Number.isNaN(opt.value)) return String(opt.value);
  if (typeof opt.text === 'string' && opt.text) {
    return opt.text
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }
  return null;
}

export function localizeMetadataOptions(propKey, options, t) {
  if (!Array.isArray(options)) return options;
  return options.map((opt) => {
    const optionKey = resolveMetadataOptionKey(opt);
    if (!optionKey) return opt;
    return {
      ...opt,
      text: t(`assets.options.${propKey}.${optionKey}`, { defaultValue: opt.text }),
    };
  });
}

/**
 * Localized component property value for read-only displays (e.g. digital twin tooltip).
 *
 * @param {function} t - i18next `t`
 * @param {object} [i18n] - i18next instance (`exists` for key lookup)
 * @param {string} propKey - schema property key
 * @param {*} value - raw property value
 * @returns {string|null}
 */
export function getComponentPropertyDisplayValue(t, i18n, propKey, value) {
  if (value == null || value === '') return null;

  if (propKey === 'type') {
    return getComponentTypeDisplayName(t, i18n, { value }) || String(value);
  }

  const optionKeys = [
    resolveMetadataOptionKey({ value }),
    resolveMetadataOptionKey({ text: value }),
    toSlug(value),
  ].filter(Boolean);

  const matchedKey = [...new Set(optionKeys)].find((key) => {
    const translationKey = `assets.options.${propKey}.${key}`;
    return i18n && typeof i18n.exists === 'function' && i18n.exists(translationKey);
  });
  if (matchedKey) {
    return t(`assets.options.${propKey}.${matchedKey}`);
  }

  return String(value);
}

/**
 * Localized component property schema for machine builder / asset template forms.
 *
 * @param {string} componentType
 * @param {{ metadata: object, t: function, i18n?: object }} ctx
 * @returns {object}
 */
export function localizeComponentPropertySchema(componentType, { metadata, t, i18n }) {
  const rawSchema = metadata
    && metadata.component_properties
    && metadata.component_properties[componentType];
  if (!rawSchema) return {};

  return Object.entries(rawSchema).reduce((acc, [propKey, propDef]) => {
    if (propKey === 'bearings' || propKey === 'locations') {
      acc[propKey] = propDef;
      return acc;
    }
    if (!propDef || typeof propDef !== 'object' || Array.isArray(propDef)) {
      acc[propKey] = propDef;
      return acc;
    }

    if (propKey === 'dimensions') {
      const localizedDimensions = _.cloneDeep(propDef);
      Object.keys(localizedDimensions).forEach((dimKey) => {
        if (_.includes(['editable', 'order', 'type'], dimKey)) return;
        const dimDef = localizedDimensions[dimKey];
        if (!dimDef || typeof dimDef !== 'object') return;
        localizedDimensions[dimKey] = {
          ...dimDef,
          display_name: getComponentPropertyFieldDisplayName(t, i18n, dimKey, dimDef),
        };
      });
      acc[propKey] = localizedDimensions;
      return acc;
    }

    const localized = _.cloneDeep(propDef);
    localized.display_name = getComponentPropertyFieldDisplayName(t, i18n, propKey, propDef);

    if (Array.isArray(localized.options)) {
      localized.options = localizeMetadataOptions(propKey, localized.options, t);
    }

    if (propKey === 'shaft_info' && localized.properties) {
      localized.properties = Object.entries(localized.properties).reduce(
        (shaftAcc, [shaftPropKey, shaftPropDef]) => {
          if (!shaftPropDef || typeof shaftPropDef !== 'object') {
            shaftAcc[shaftPropKey] = shaftPropDef;
            return shaftAcc;
          }
          const localizedShaft = _.cloneDeep(shaftPropDef);
          localizedShaft.display_name = getComponentPropertyFieldDisplayName(
            t,
            i18n,
            shaftPropKey,
            shaftPropDef
          );
          if (Array.isArray(localizedShaft.options)) {
            localizedShaft.options = localizeMetadataOptions(shaftPropKey, localizedShaft.options, t);
          }
          shaftAcc[shaftPropKey] = localizedShaft;
          return shaftAcc;
        },
        {}
      );
    }

    acc[propKey] = localized;
    return acc;
  }, {});
}
